"use client"

import React, { useState, useEffect } from 'react'
import { ethers } from 'ethers'
import { EthereumProvider } from '@walletconnect/ethereum-provider'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faClock, faPaperPlane, faQrcode, faUsers, faCopy, faSun, faMoon, faChevronLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { motion, AnimatePresence } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { useSwipeable } from 'react-swipeable'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

const MOBILITY_TOKEN_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
]
const MOBILITY_TOKEN_ADDRESS = "0xa7CD71bF402b871E5f60ee01eA6a03753A6cD0d1" // Replace with actual Sepolia address

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  timestamp: number;
}

interface Friend {
  name: string;
  address: string;
}

export default function Component() {
  const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null)
  const [address, setAddress] = useState<string>("")
  const [balance, setBalance] = useState<string>("0")
  const [recipient, setRecipient] = useState<string>("")
  const [amount, setAmount] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [currentPrice, setCurrentPrice] = useState<string>("0.00")
  const [activeTab, setActiveTab] = useState<'home' | 'history' | 'send' | 'receive' | 'friends'>('home')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [friends, setFriends] = useState<Friend[]>([])
  const [newFriendName, setNewFriendName] = useState<string>("")
  const [newFriendAddress, setNewFriendAddress] = useState<string>("")
  const [isAddFriendOpen, setIsAddFriendOpen] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('isDarkMode')
      return saved !== null ? JSON.parse(saved) : true
    }
    return true
  })
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedCurrency') || 'usd'
    }
    return 'usd'
  })

  useEffect(() => {
    localStorage.setItem('isDarkMode', JSON.stringify(isDarkMode))
  }, [isDarkMode])
  
  useEffect(() => {
    localStorage.setItem('selectedCurrency', selectedCurrency)
  }, [selectedCurrency])

  useEffect(() => {
    if (address) {
      updateBalance()
    }
  }, [address])

  useEffect(() => {
    fetchEthereumPrice()
    const interval = setInterval(fetchEthereumPrice, 30000) // Update every 30 seconds
    return () => clearInterval(interval)
  }, [selectedCurrency])

  useEffect(() => {
    // Load transactions and friends from local storage
    const storedTransactions = localStorage.getItem('transactions')
    if (storedTransactions) {
      setTransactions(JSON.parse(storedTransactions))
    }
    const storedFriends = localStorage.getItem('friends')
    if (storedFriends) {
      setFriends(JSON.parse(storedFriends))
    }
  }, [])

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      const tabs = ['home', 'history', 'send', 'receive', 'friends']
      const currentIndex = tabs.indexOf(activeTab)
      const nextIndex = (currentIndex + 1) % tabs.length
      setActiveTab(tabs[nextIndex] as typeof activeTab)
    },
    onSwipedRight: () => {
      const tabs = ['home', 'history', 'send', 'receive', 'friends']
      const currentIndex = tabs.indexOf(activeTab)
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length
      setActiveTab(tabs[prevIndex] as typeof activeTab)
    },
    trackMouse: true,
  })

  async function connectWallet() {
    try {
      const wcProvider = await EthereumProvider.init({
        projectId: "2b1d0a81024dbf6c308c27e16fa4576e", // Get this from https://cloud.walletconnect.com/
        chains: [11155111], // Sepolia testnet
        showQrModal: true
      })

      await wcProvider.enable()
      const web3Provider = new ethers.providers.Web3Provider(wcProvider as any)
      setProvider(web3Provider)

      const signer = web3Provider.getSigner()
      const address = await signer.getAddress()
      setAddress(address)
      setError("")
    } catch (error) {
      console.error("Failed to connect wallet:", error)
      setError("Failed to connect wallet")
    }
  }

  async function updateBalance() {
    if (!provider || !address) return
    try {
      const contract = new ethers.Contract(MOBILITY_TOKEN_ADDRESS, MOBILITY_TOKEN_ABI, provider)
      const balance = await contract.balanceOf(address)
      setBalance(ethers.utils.formatUnits(balance, 18)) // Again, assuming 18 decimals
      setError("")
    } catch (error) {
      console.error("Failed to fetch balance:", error)
      setError("Failed to fetch balance. Please verify contract deployment and network connection.")
      setBalance("0")
    }
  }

  async function handleTransfer() {
    if (!provider || !recipient || !amount) {
      setError("Please fill in all fields.");
      return;
    }
  
    setIsAuthenticating(true);
    const isAuthenticated = await authenticateUser();
    setIsAuthenticating(false);
  
    if (!isAuthenticated) {
      setError("Authentication failed. Please try again.");
      return;
    }
  
    try {
      const signer = provider.getSigner();
      const contract = new ethers.Contract(MOBILITY_TOKEN_ADDRESS, MOBILITY_TOKEN_ABI, signer);
  
      console.log("Sending to:", recipient);
      console.log("Amount:", amount);
  
      const amountInTokenUnits = ethers.utils.parseUnits(amount, 18);
  
      const tx = await contract.transfer(recipient, amountInTokenUnits);
  
      await tx.wait();
  
      // Add transaction to history
      const newTransaction: Transaction = {
        hash: tx.hash,
        from: address,
        to: recipient,
        value: amount,
        timestamp: Date.now(),
      }
      const updatedTransactions = [...transactions, newTransaction]
      setTransactions(updatedTransactions)
      localStorage.setItem('transactions', JSON.stringify(updatedTransactions))
  
      await updateBalance();
  
      setRecipient("");
      setAmount("");
      setError("");
      setActiveTab('home');
    } catch (error) {
      console.error("Transfer failed:", error);
      setError("Transfer failed. Please check your balance and recipient address.");
    }
  }

  async function fetchEthereumPrice() {
    try {
      const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=${selectedCurrency}`)
      const data = await response.json()
      setCurrentPrice(data.ethereum[selectedCurrency].toFixed(2))
    } catch (error) {
      console.error("Failed to fetch Ethereum price:", error)
    }
  }

  function addFriend() {
    if (newFriendName && newFriendAddress) {
      const newFriend: Friend = { name: newFriendName, address: newFriendAddress }
      const updatedFriends = [...friends, newFriend]
      setFriends(updatedFriends)
      localStorage.setItem('friends', JSON.stringify(updatedFriends))
      setNewFriendName("")
      setNewFriendAddress("")
      setIsAddFriendOpen(false)
    }
  }

  function removeFriend(addressToRemove: string) {
    const updatedFriends = friends.filter(friend => friend.address !== addressToRemove)
    setFriends(updatedFriends)
    localStorage.setItem('friends', JSON.stringify(updatedFriends))
  }

  async function authenticateUser() {
    // Simulating authentication process
    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        const isAuthenticated = Math.random() > 0.2; // 80% success rate
        resolve(isAuthenticated);
      }, 2000);
    });
  }

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div {...handlers} className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-b from-[#0c0c0c] to-[#0c0c0c] text-[#ffffff]' : 'bg-gradient-to-b from-[#ffffff] to-[#ffffff] text-[#18181b]'} p-4 font-['Hubot_Sans']`}>
      <div className="max-w-md mx-auto space-y-6 pb-20">
        {/* Header */}
        <motion.div
          className="flex items-center justify-between mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className={`text-3xl font-bold bg-clip-text text-transparent ${isDarkMode ? 'bg-gradient-to-r from-[#ffffff] to-[#ffffff]' : 'bg-gradient-to-r from-[#18181b] to-[#18181b]'}`}>Aerway</h1>
          <div className="flex items-center space-x-2">
            {address && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigator.clipboard.writeText(address)}
                className={`p-0 bg-transparent hover:bg-transparent active:bg-transparent border-none ${isDarkMode ? 'text-[#ffffff] hover:text-[#ffffff]' : 'text-[#18181b] hover:text-[#0c0c0c]'}`}
              >
                <FontAwesomeIcon icon={faCopy} className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className={`p-0 bg-transparent hover:bg-transparent active:bg-transparent border-none ${isDarkMode ? 'text-[#ffffff] hover:text-[#ffffff]' : 'text-[#18181b] hover:text-[#0c0c0c]'}`}
            >
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="h-5 w-5" />
            </Button>
          </div>
        </motion.div>

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`${isDarkMode ? 'bg-gradient-to-br from-[#ffffff] to-[#ffffff] text-[#18181b]' : 'bg-gradient-to-br from-[#18181b] to-[#18181b] text-[#ffffff]'} mb-4 ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                <CardHeader className="p-4">
                  <CardTitle className="text-lg font-normal">Your Balance</CardTitle>
                  <motion.div
                    className="text-4xl font-bold mt-1"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {parseFloat(balance).toFixed(2)} AER
                  </motion.div>
                  <div className={`text-sm ${isDarkMode ? 'text-[#3f3f46]' : 'text-[#d4d4d8]'} mt-1`}>
                    {address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "Not connected"}
                  </div>
                </CardHeader>
              </Card>

              <Card className={`${isDarkMode ? 'bg-[#0c0c0c] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'} mb-4`}>
                <CardHeader className="p-4">
                  <CardTitle className="text-lg font-normal">Current Price (Ethereum)</CardTitle>
                  <div className="flex items-center left">
                    <motion.div
                      className="text-3xl font-bold mt-1"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      {currentPrice}
                    </motion.div>
                    <select
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className={`ml-2 p-1 rounded-xl text-l border-[1px] ${isDarkMode ? 'bg-[#18181b] text-[#ffffff] border-[#27272a]' : 'bg-[#f4f4f5] text-[#18181b] border-[#e4e4e7]'}`}
                    >
                      <option value="usd">USD</option>
                      <option value="inr">INR</option>
                      <option value="eur">EUR</option>
                      <option value="gbp">GBP</option>
                    </select>
                  </div>
                  <CardDescription className={`${isDarkMode ? 'text-[#d4d4d8]' : 'text-[#3f3f46]'} text-sm`}>Updates every 30 seconds</CardDescription>
                </CardHeader>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <Card className={`${isDarkMode ? 'bg-[#0c0c0c] hover:bg-[#3f3f46]' : 'bg-[#ffffff] hover:bg-[#d4d4d8]'} transition-colors cursor-pointer ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`} onClick={() => setActiveTab('send')}>
                  <CardContent className="p-4 text-center">
                    <FontAwesomeIcon icon={faPaperPlane} className={`h-6 w-6 mx-auto mb-2 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                    <div className={isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}>Send</div>
                  </CardContent>
                </Card>
                <Card className={`${isDarkMode ? 'bg-[#0c0c0c] hover:bg-[#3f3f46]' : 'bg-[#ffffff] hover:bg-[#d4d4d8]'} transition-colors cursor-pointer ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`} onClick={() => setActiveTab('receive')}>
                  <CardContent className="p-4 text-center">
                    <FontAwesomeIcon icon={faQrcode} className={`h-6 w-6 mx-auto mb-2 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                    <div className={isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}>Receive</div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          )}

          {activeTab === 'send' && (
            <motion.div
              key="send"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`${isDarkMode ? 'bg-[#18181b] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                <CardHeader className="flex flex-row items-center p-4">
                  <Button variant="ghost" size="icon" onClick={() => setActiveTab('home')} className={`mr-2 ${isDarkMode ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d4d4d8]'}`}>
                    <FontAwesomeIcon icon={faChevronLeft} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                  </Button>
                  <CardTitle>Send AER</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-4">
                  <Input
                    placeholder="Recipient Address"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    className={`${isDarkMode ? 'bg-[#18181b] border-[#27272a] text-[#ffffff]' : 'bg-[#ffffff] border-[#e4e4e7] text-[#18181b]'}`}
                  />
                  <Input
                    placeholder="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`${isDarkMode ? 'bg-[#18181b] border-[#27272a] text-[#ffffff]' : 'bg-[#ffffff] border-[#e4e4e7] text-[#18181b]'}`}
                  />
                  <Button 
                    onClick={handleTransfer} 
                    className={`w-full ${isDarkMode ? 'bg-[#ffffff] text-[#18181b] hover:bg-[#d4d4d8]' : 'bg-[#18181b] text-[#ffffff] hover:bg-[#3f3f46]'}`}
                    disabled={isAuthenticating}
                  >
                    {isAuthenticating ? 'Authenticating...' : 'Send'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === 'receive' && (
            <motion.div
              key="receive"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`${isDarkMode ? 'bg-[#18181b] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                <CardHeader className="flex flex-row items-center p-4">
                  <Button variant="ghost" size="icon" onClick={() => setActiveTab('home')} className={`mr-2 ${isDarkMode ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d4d4d8]'}`}>
                    <FontAwesomeIcon icon={faChevronLeft} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                  </Button>
                  <CardTitle>Receive AER</CardTitle>
                </CardHeader>
                <CardContent className="text-center p-4">
                  {address && (
                    <div className="bg-white p-4 rounded-lg inline-block mb-4">
                      <QRCodeSVG value={address} size={200} />
                    </div>
                  )}
                  <p className="break-all mb-4">{address}</p>
                  <Button
                    onClick={() => navigator.clipboard.writeText(address)}
                    className={`${isDarkMode ? 'bg-[#ffffff] text-[#18181b] hover:bg-[#d4d4d8]' : 'bg-[#18181b] text-[#ffffff] hover:bg-[#3f3f46]'}`}
                  >
                    Copy Address
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`${isDarkMode ? 'bg-[#18181b] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                <CardHeader className="flex flex-row items-center p-4">
                  <Button variant="ghost" size="icon" onClick={() => setActiveTab('home')} className={`mr-2 ${isDarkMode ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d4d4d8]'}`}>
                    <FontAwesomeIcon icon={faChevronLeft} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                  </Button>
                  <CardTitle>Transaction History</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-4">
                  {transactions.length === 0 ? (
                    <p className={`text-center ${isDarkMode ? 'text-[#d4d4d8]' : 'text-[#3f3f46]'}`}>No transactions yet</p>
                  ) : (
                    transactions.map((tx, index) => (
                      <Card key={index} className={`${isDarkMode ? 'bg-[#18181b]' : 'bg-[#ffffff]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                        <CardContent className="p-4">
                          <p className={`text-sm ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`}>From: {tx.from.slice(0, 6)}...{tx.from.slice(-4)}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`}>To: {tx.to.slice(0, 6)}...{tx.to.slice(-4)}</p>
                          <p className={`text-lg ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'} font-semibold`}>{tx.value} AER</p>
                          <p className={`text-xs ${isDarkMode ? 'text-[#d4d4d8]' : 'text-[#3f3f46]'}`}>{new Date(tx.timestamp).toLocaleString()}</p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === 'friends' && (
            <motion.div
              key="friends"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`${isDarkMode ? 'bg-[#18181b] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                <CardHeader className="flex flex-row items-center justify-between p-4">
                  <div className="flex items-center">
                    <Button variant="ghost" size="icon" onClick={() => setActiveTab('home')} className={`mr-2 ${isDarkMode ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d4d4d8]'}`}>
                      <FontAwesomeIcon icon={faChevronLeft} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                    </Button>
                    <CardTitle>Friends</CardTitle>
                  </div>
                  <Dialog open={isAddFriendOpen} onOpenChange={setIsAddFriendOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className={isDarkMode ? 'hover:bg-[#3f3f46]' : 'hover:bg-[#d4d4d8]'}>
                        <FontAwesomeIcon icon={faPlus} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className={`${isDarkMode ? 'bg-[#18181b] text-[#ffffff]' : 'bg-[#ffffff] text-[#18181b]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                      <DialogHeader>
                        <DialogTitle>Add New Friend</DialogTitle>
                        <DialogDescription>Enter your friend's details below.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <Input
                          placeholder="Friend's Name"
                          value={newFriendName}
                          onChange={(e) => setNewFriendName(e.target.value)}
                          className={`${isDarkMode ? 'bg-[#18181b] border-[#27272a] text-[#ffffff]' : 'bg-[#ffffff] border-[#e4e4e7] text-[#18181b]'}`}
                        />
                        <Input
                          placeholder="Friend's Address"
                          value={newFriendAddress}
                          onChange={(e) => setNewFriendAddress(e.target.value)}
                          className={`${isDarkMode ? 'bg-[#18181b] border-[#27272a] text-[#ffffff]' : 'bg-[#ffffff] border-[#e4e4e7] text-[#18181b]'}`}
                        />
                        <Button onClick={addFriend} className={`w-full ${isDarkMode ? 'bg-[#ffffff] text-[#18181b] hover:bg-[#d4d4d8]' : 'bg-[#18181b] text-[#ffffff] hover:bg-[#3f3f46]'}`}>
                          Add Friend
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent className="space-y-4 p-4">
                  {friends.length === 0 ? (
                    <p className={`text-center ${isDarkMode ? 'text-[#d4d4d8]' : 'text-[#3f3f46]'}`}>No friends added yet</p>
                  ) : (
                    friends.map((friend, index) => (
                      <Card key={index} className={`${isDarkMode ? 'bg-[#18181b]' : 'bg-[#ffffff]'} ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'}`}>
                        <CardContent className="p-4 flex justify-between items-center">
                          <div>
                            <p className={`font-semibold ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`}>{friend.name}</p>
                            <p className={`text-sm ${isDarkMode ? 'text-[#d4d4d8]' : 'text-[#3f3f46]'}`}>{friend.address.slice(0, 6)}...{friend.address.slice(-4)}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button onClick={() => {
                              setRecipient(friend.address)
                              setActiveTab('send')
                            }} className={`${isDarkMode ? 'bg-[#ffffff] text-[#18181b] hover:bg-[#d4d4d8]' : 'bg-[#18181b] text-[#ffffff] hover:bg-[#3f3f46]'}`}>
                              Send
                            </Button>
                            <Button onClick={() => removeFriend(friend.address)} variant="ghost" size="icon">
                              <FontAwesomeIcon icon={faTrash} className={`h-5 w-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-[#18181b]'}`} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Display */}
        {error && (
          <motion.div
            className="text-red-500 text-sm mt-2 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {error}
          </motion.div>
        )}

        {/* Connect Button */}
        {!address && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Button
              onClick={connectWallet}
              className={`w-full ${isDarkMode ? 'bg-gradient-to-r from-[#ffffff] to-[#ffffff] text-[#18181b] hover:from-[#ffffff] hover:to-[#d4d4d8]' : 'bg-gradient-to-r from-[#18181b] to-[#18181b] text-[#ffffff] hover:from-[#3f3f46] hover:to-[#3f3f46]'}`}
            >
              Connect Wallet
            </Button>
          </motion.div>
        )}

        {/* Navigation Menu */}
        <motion.div
            className={`fixed bottom-0 left-0 right-0 ${isDarkMode ? 'bg-[#18181b]' : 'bg-[#f4f4f5]'} px-4 py-3 border-t ${isDarkMode ? 'border-[#27272a]' : 'border-[#e4e4e7]'} rounded-t-[12px]`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-md mx-auto flex justify-between items-center">
              {['home', 'history', 'send', 'receive', 'friends'].map((tab) => (
                <Button
                  key={tab}
                  variant="ghost"
                  className={`${isDarkMode ? 'text-[#ffffff] hover:bg-[#3f3f46]' : 'text-[#18181b] hover:bg-[#d4d4d8]'} px-4 py-2 rounded-[12px] ${activeTab === tab ? (isDarkMode ? "bg-[#3f3f46]" : "bg-[#d4d4d8]") : ""}`}
                  onClick={() => setActiveTab(tab as typeof activeTab)}
                >
                  {tab === 'home' && <FontAwesomeIcon icon={faHome} className="h-5 w-5" style={{ color: isDarkMode ? "#ffffff" : "#18181b" }} />}
                  {tab === 'history' && <FontAwesomeIcon icon={faClock} className="h-5 w-5" style={{ color: isDarkMode ? "#ffffff" : "#18181b" }} />}
                  {tab === 'send' && <FontAwesomeIcon icon={faPaperPlane} className="h-5 w-5" style={{ color: isDarkMode ? "#ffffff" : "#18181b" }} />}
                  {tab === 'receive' && <FontAwesomeIcon icon={faQrcode} className="h-5 w-5" style={{ color: isDarkMode ? "#ffffff" : "#18181b" }} />}
                  {tab === 'friends' && <FontAwesomeIcon icon={faUsers} className="h-5 w-5" style={{ color: isDarkMode ? "#ffffff" : "#18181b" }} />}
                </Button>
              ))}
            </div>
          </motion.div>
      </div>
    </div>
  )
}